import { useEffect, useState } from "react";
import { LayoutTemplate, CalendarDays, Plus, Trash2, Loader2, BookmarkPlus } from "lucide-react";
import { api, errText } from "../../lib/api";
import { HERO_TEMPLATES, nextWindow, rollForward } from "../../lib/heroTemplates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { newSlide } from "./HeroSlides";
import { toast } from "sonner";

const fmtRange = (s, e) => {
  if (!s && !e) return "No schedule";
  const f = (v) => new Date(`${v}:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  return `${s ? f(s) : "Now"} → ${e ? f(e) : "No end"}`;
};

const TemplateCard = ({ t, win, onAdd, onDelete, testid, disabled, addLabel = "Add slide" }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden flex flex-col hover:border-[#F5551E] transition-colors" data-testid={testid}>
    <div className="bg-[#12234A] text-white p-3 min-h-[104px]">
      <p className="text-[10px] font-semibold text-white/70 truncate">{t.emoji ? `${t.emoji} ` : ""}{t.badge}</p>
      <p className="font-display font-extrabold leading-tight mt-1 text-sm">{t.title_line1}<br /><span className="text-[#F5551E]">{t.title_line2}</span></p>
      {(t.buttons || []).length > 0 && (
        <div className="flex gap-1 mt-2">{t.buttons.map((b, i) => <span key={i} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${i === 0 ? "bg-[#F5551E]" : "border border-white/40"}`}>{b.label}</span>)}</div>
      )}
    </div>
    <div className="p-3 flex-1 flex flex-col">
      <p className="text-sm font-semibold text-[#12234A]">{t.name}</p>
      <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><CalendarDays size={12} /> {fmtRange(win.start_at, win.end_at)}</p>
      <div className="mt-auto pt-2 flex gap-1.5">
        <Button size="sm" onClick={onAdd} disabled={disabled} data-testid={`${testid}-add`} className="flex-1 h-8 rounded-lg bg-[#F5551E] hover:bg-[#E04812] text-white text-xs gap-1"><Plus size={13} /> {addLabel}</Button>
        {onDelete && <button onClick={onDelete} data-testid={`${testid}-delete`} title="Delete template" className="h-8 w-8 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 flex items-center justify-center"><Trash2 size={13} /></button>}
      </div>
    </div>
  </div>
);

// Gallery of built-in seasonal templates + the admin's saved templates; picking one appends a pre-scheduled slide.
export function TemplatesGallery({ open, onOpenChange, onPick, disabled, noToast = false, title = "Slide templates", addLabel = "Add slide", description = "Each template is pre-scheduled for its next upcoming date (Toronto time). You can edit everything before saving.", maxNote = "You already have the maximum of 12 slides. Delete one to add a template." }) {
  const [mine, setMine] = useState(null);
  useEffect(() => { if (open) api.get("/admin/hero-templates").then((r) => setMine(r.data)).catch(() => setMine([])); }, [open]);

  const pick = (t, win) => {
    const { key, name, emoji, rule, ...text } = t;
    const { id, ...clean } = text;
    onPick({ ...newSlide({ ...clean, ...win, enabled: true, duration: t.duration || 6 }), template_key: key || "" }, name);
    onOpenChange(false);
    if (!noToast) toast.success(`"${name}" added — review it, then click Save`);
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/hero-templates/${id}`); setMine((m) => m.filter((x) => x.id !== id)); toast.success("Template deleted"); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white" data-testid="hero-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate size={18} className="text-[#F5551E]" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {disabled && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="templates-max-note">{maxNote}</p>}
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-2">My templates</p>
        {mine === null ? <Loader2 size={18} className="animate-spin text-slate-400" /> : !mine.length ? (
          <p className="text-sm text-slate-400" data-testid="hero-templates-mine-empty">No saved templates yet. Use "Save as template" on any slide.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hero-templates-mine">
            {mine.map((m) => {
              const win = rollForward(m.slide.start_at, m.slide.end_at);
              return <TemplateCard key={m.id} t={{ ...m.slide, name: m.name }} win={win} testid={`hero-template-mine-${m.id}`} disabled={disabled} addLabel={addLabel}
                onAdd={() => !disabled && pick({ ...m.slide, name: m.name }, win)} onDelete={() => remove(m.id)} />;
            })}
          </div>
        )}
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-4">Seasonal & holidays</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hero-templates-builtin">
          {HERO_TEMPLATES.map((t) => {
            const win = nextWindow(t.rule);
            return <TemplateCard key={t.key} t={t} win={win} testid={`hero-template-${t.key}`} disabled={disabled} addLabel={addLabel} onAdd={() => !disabled && pick(t, win)} />;
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SaveAsTemplate({ slide }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/hero-templates", { name, slide });
      toast.success("Saved as template");
      setOpen(false); setName("");
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <button type="button" onClick={() => { setName(`${slide.title_line1} ${slide.title_line2}`.trim().slice(0, 60)); setOpen(true); }} data-testid="hero-save-template-btn"
      className="text-xs font-semibold text-[#12234A] flex items-center gap-1 hover:text-[#F5551E]"><BookmarkPlus size={14} /> Save as template</button>
  );
  return (
    <div className="flex items-center gap-1.5" data-testid="hero-save-template-form">
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Template name" data-testid="hero-save-template-name" className="h-8 w-44 rounded-lg text-xs" />
      <Button size="sm" onClick={save} disabled={busy || !name.trim()} data-testid="hero-save-template-confirm" className="h-8 rounded-lg bg-[#12234A] text-white text-xs">{busy ? <Loader2 size={13} className="animate-spin" /> : "Save"}</Button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-500">Cancel</button>
    </div>
  );
}
